import { useMutation } from '@tanstack/react-query'
import { differenceInCalendarDays } from 'date-fns'
import { ChangeEvent, useEffect, useState } from 'react'
import { FaTrashAlt } from 'react-icons/fa'
import { FaX } from 'react-icons/fa6'
import { MdOutlineImageNotSupported } from 'react-icons/md'
import { SiGooglemaps } from 'react-icons/si'

import { useAppSelector } from '../hooks/useAppSelector'
import { useDialogBox } from '../hooks/useDialogBox'
import { useGeolocation } from '../hooks/useGeolocation'
import { usePromotersStayingTimeContext } from '../hooks/usePromotersStayingTimeContext'
import { queryClient } from '../libs/reactQuery'
import { cn } from '../libs/tailwindMerge'
import { FileImage, GetReportsRequest, Report, uploadImages } from '../services/reportServices'
import { compressImage } from '../utils/compressImage'
import { generateUniqueFileName } from '../utils/generateUniqueFileName'
import { Button } from './Button'
import { ModalAddImagesSkeleton } from './ModalAddImagesSkeleton'

interface Coordinates {
  latitude: number
  longitude: number
}

interface ModalAddImagesProps {
  report: Report | null
  onClose: () => void
  filters?: Omit<GetReportsRequest, 'page'>
}

export function ModalAddImages({ report, onClose, filters }: ModalAddImagesProps) {
  const [newImagesFilesList, setNewImagesFilesList] = useState<FileImage[]>([])
  const [previews, setPreviews] = useState<string[]>([])
  const [toBeRemoved, setToBeRemoved] = useState<string[]>([])
  const [coordinates, setCoordinates] = useState<Coordinates | null>(null)

  const [isEdited, setIsEdited] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [slowConnectionMessage, setSlowConnectionMessage] = useState('Carregando')
  const [isSubmiting, setIsSubmiting] = useState(false)
  const [progress, setProgress] = useState(0)

  const { showDialog } = useDialogBox()
  const getGeolocation = useGeolocation()

  const { profile } = useAppSelector(state => state.auth)

  const { activeCycle } = usePromotersStayingTimeContext()

  function handleClose() {
    if (isLoading) return
    if (isEdited) {
      const confirmation = confirm('Você fez alterações, tem certeza que deseja descartar as alterações?')
      if (!confirmation) return
    }
    handleCloseModal()
  }

  function handleCloseModal() {
    if (isLoading) return
    previews.forEach(prev => URL.revokeObjectURL(prev))
    onClose()
  }

  async function handleAddImages(event: ChangeEvent<HTMLInputElement>) {
    if (!coordinates) {
      showDialog({
        title: 'Não foi possível recuperar sua localização!',
      })
      return
    }
    if (event.target.files && event.target.files.length > 0) {
      setIsEdited(true)
    }
    if (event.target.files) {
      const newFiles = Array.from(event.target.files).map(async file => {
        if (!file.type.includes('image/')) {
          return null
        }
        const renamedFile = new File([file], generateUniqueFileName(file), {
          type: file.type,
        })
        const compressedFile = await compressImage(renamedFile)
        return compressedFile
      })

      const compressedFiles = await Promise.all(newFiles)
      const validFiles = compressedFiles
        .filter(file => file !== null)
        .map(file => ({
          file,
          latitude: coordinates.latitude,
          longitude: coordinates.longitude,
          user: {
            id: profile?.id,
            name: profile?.name,
            avatar_url: profile?.avatar_url,
          },
        }))

      const newPreviews = validFiles.map(fileImage => URL.createObjectURL(fileImage.file))

      setNewImagesFilesList(prev => [...validFiles, ...prev])
      setPreviews(prev => [...newPreviews, ...prev])
    }
  }

  function handleRemoveImage(index: number, file: FileImage) {
    const confirmRemove = confirm('Tem certeza que deseja remover essa imagem?')
    if (!confirmRemove) return

    if (file.id) {
      const toRemove = file.file
        ? file.file.name
        : previews[index].split('/').pop()
      if (toRemove) {
        setToBeRemoved(prev => [...prev, toRemove])
      }
    }

    setNewImagesFilesList(prev => prev.filter((_, i) => i !== index))
    setPreviews(prev => prev.filter((_, i) => i !== index))
    setIsEdited(true)
  }

  const { mutate: submitNewImages } = useMutation({
    mutationFn: async () => {
      setProgress(0)

      const result = await uploadImages(newImagesFilesList, report!.id, (uploaded, total) => {
        setProgress(Math.round((uploaded / total) * 100))
      }, toBeRemoved)

      if (!result.success) {
        throw new Error(result.message)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['reports', filters],
      })
      queryClient.refetchQueries({
        queryKey: ['reports', filters],
      })
    },
    onError: (error) => {
      console.log(error)
    },
  })

  async function confirmPromoterSpotOpenBeforAddImages() {
    if (profile?.role === 'PROMOTER') {
      if (activeCycle) {
        if (activeCycle.reportId !== report?.id) {
          showDialog({
            title: 'Você tem um registro aberto em outra loja!',
            description: 'Para editar as imagens deste relatório, é preciso primeiro dar entrada nesta loja 👍',
            type: 'warning',
          })
          return
        }
      } else {
        showDialog({
          title: 'Você ainda não deu entrada nessa loja!',
          description: 'Para editar as imagens deste relatório, é preciso primeiro dar entrada na loja 👍',
          type: 'warning',
        })
        return
      }
    }
    await handleSubmitNewImageList()
  }

  async function handleSubmitNewImageList() {
    if (!report) return
    setIsSubmiting(true)
    submitNewImages(undefined, {
      onSuccess: () => {
        showDialog({
          title: 'Novas imagens salvas com sucesso',
          type: 'success',
          cancelAction: () => handleCloseModal(),
        })
      },
      onError: error => {
        showDialog({
          title: 'Erro ao salvar nova lista de imagens!',
          content: (
            <>
              <p className="text-sm text-gray-500">Houve um erro ao tentar salvar sua lista de imagens.</p>
              <p className="text-sm text-gray-500">Mas o sistema salvou essa lista e você pode solicitar o reenvio delas mais tarde 👍.</p>
              <details>
                <summary className="font-semibold text-sm text-gray-500">Erro</summary>
                <p className="text-sm text-gray-500">{error.message}</p>
              </details>
            </>
          ),
          cancelAction: () => handleCloseModal(),
        })
      },
      onSettled: () => {
        setIsSubmiting(false)
      },
    })
    setIsEdited(false)
  }

  useEffect(() => {
    if (!report) return
    setIsLoading(true)
    setPreviews(report.report_images.map(item => item.url))
    setNewImagesFilesList(report.report_images.map(item => ({
      id: item.id,
      latitude: item.latitude,
      longitude: item.longitude,
      user: item.user,
      created_at: new Date(item.created_at),
    })))
    setIsLoading(false)
  }, [report])

  useEffect(() => {
    const messages = [
      'Por favor aguarde...',
      'Identificando sua localização...',
      'Parece estar tomando mais tempo do que deveria...',
      'Não se preocupe 🫣...',
      'Ainda estamos tentando...',
    ]
    let messageIndex = 0
    const messagesInterval = setInterval(() => {
      if (messageIndex < messages.length) {
        setSlowConnectionMessage(messages[messageIndex])
        messageIndex++
      } else {
        messageIndex = 0
      }
    }, 5000)
    setIsLoading(true)
    getGeolocation()
      .then(coord => setCoordinates(coord))
      .catch(err => {
        console.log(err)
      })
      .finally(() => {
        setIsLoading(false)
        clearInterval(messagesInterval)
      })
  }, [getGeolocation])

  if (!report) return <></>

  return (
    <div
      className="fixed top-0 left-0 w-screen h-screen bg-black/65 flex items-center justify-center z-[10000]"
      onClick={handleClose}
    >
      <div
        className="flex flex-col bg-slate-100 w-[90vw] h-[90dvh] relative rounded-md p-2"
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          className="absolute right-2 top-2"
          variant="ghost"
          size="icon"
          onClick={handleClose}
        >
          <FaX />
        </Button>
        <header className="justify-between">
          <div className="flex flex-col">
            <strong className="text-lg max-w-[70%]">
              Images do {report.store.name}
            </strong>
            <span
              className="text-slate-700 font-semibold"
            >
              {report.store.city} - {report.store.state}
            </span>
          </div>
        </header>
        <div className="flex flex-col md:items-end gap-4 md:flex-row my-8 w-full">
          <div className="w-full sm:max-w-[50%] lg:max-w-[25%]">
            <label
              className="block mb-2 text-sm font-semibold text-gray-900"
              htmlFor="images_input"
            >
              {isLoading
                ? slowConnectionMessage
                : 'Escolher imagem'}
            </label>
            <input
              className="block w-full text-sm text-gray-900 border border-gray-300 bg-white file:p-2 disabled:file:bg-gray-400 file:bg-indigo-600 file:cursor-pointer disabled:file:text-gray-700 file:text-white file: rounded-md cursor-pointer file:border-0"
              id="images_input"
              onChange={handleAddImages}
              disabled={isLoading}
              type="file"
              capture="environment"
              multiple
              accept="image/*"
            />
          </div>
          {isSubmiting && (
            <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
              <div className="bg-indigo-600 text-xs font-medium text-gray-100 text-center p-0.5 leading-none rounded-full" style={{ width: `${progress}%` }}> {progress}%</div>
            </div>
          )}
        </div>
        <main className="flex-1 overflow-y-auto">
          {isLoading
            ? (
              <ModalAddImagesSkeleton />
              )
            : (
              <>
                {previews.length === 0 &&
                  <div className="flex w-full p-4 items-center justify-center border-2 rounded-md mt-4">
                    <span className="flex flex-col items-center font-semibold text-gray-500">
                      <MdOutlineImageNotSupported size={55} />
                      Nenhuma imagem ainda
                    </span>
                  </div>}
                {previews.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-4">
                    {previews.map((src, index) => (
                      <div
                        key={index}
                        className="overflow-hidden rounded-md relative"
                      >
                        <img
                          className="object-contain"
                          src={src}
                          loading="lazy"
                        />
                        {newImagesFilesList[index].created_at && (
                          <span className={cn(
                            'flex items-center justify-center text-white absolute h-12 w-8 border border-t-0 rounded-b-full top-0 left-4',
                            differenceInCalendarDays(new Date(), newImagesFilesList[index].created_at) < 1 && 'border-green-400 bg-green-600/80',
                            differenceInCalendarDays(new Date(), newImagesFilesList[index].created_at) === 1 && 'border-amber-400 bg-amber-600/80',
                            differenceInCalendarDays(new Date(), newImagesFilesList[index].created_at) > 1 && 'border-red-400 bg-red-600/80',
                          )}
                          >
                            {differenceInCalendarDays(new Date(), newImagesFilesList[index].created_at)}
                          </span>
                        )}
                        <div className="absolute top-2 right-2 flex gap-2">
                          <a
                            className="rounded-lg flex items-center justify-center p-3 bg-white/50 hover:bg-yellow-500/50 hover:text-white"
                            href={`https://www.google.com/maps?q=${newImagesFilesList[index].latitude},${newImagesFilesList[index].longitude}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <SiGooglemaps />
                          </a>
                          <button
                            className="rounded-lg flex items-center justify-center p-3 bg-white/50 hover:bg-red-500/50 hover:text-white"
                            onClick={() => handleRemoveImage(index, newImagesFilesList[index])}
                          >
                            <FaTrashAlt />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
              )}

        </main>
        <footer className="pt-4">
          <Button
            isLoading={isSubmiting || isLoading}
            className="disabled:bg-gray-400 disabled:hover:bg-gray-400 disabled:text-gray-800"
            onClick={() => confirmPromoterSpotOpenBeforAddImages()}
          >
            Salvar
          </Button>
        </footer>
      </div>
    </div>
  )
}
